/**
 * Automatisches Speichern des geöffneten Termins (14.10.2026, Benjamin: „Den
 * Speichern-Button entfernen — es muss sichergestellt sein, dass jede Änderung
 * gespeichert wird, die der Nutzer an der Karte vornimmt").
 *
 * - Jede Änderung wird nach kurzer Ruhe (1,2 s) gespeichert, beim Verlassen sofort.
 * - Es läuft immer nur EINE Anfrage; Änderungen währenddessen folgen danach.
 * - Schlägt ein Speichern fehl (oder wird eine Konfliktfrage abgebrochen), wird
 *   derselbe Stand nicht endlos wiederholt — erst die nächste Änderung oder
 *   „Erneut versuchen" stößt wieder an. Der Termin bleibt dann offen.
 * - Wer den Termin schließt, während noch etwas aussteht, verliert nichts: beim
 *   Aushängen wird ein letztes Mal gesichert.
 *
 * Personen gehen nur mit dem hinaus, was sich wirklich geändert hat
 * (`slotAenderungen`). Der Server schreibt bei bestehenden Einsätzen nur fort,
 * was mitkommt — so überschreibt ein offener Termin keine Zusage, die ein Helfer
 * inzwischen in der Team-App gegeben hat.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { UpdateTerminSlot } from "../../hooks/usePlanung";

export type SichernAnzeige = "gespeichert" | "speichert" | "fehler";

export function useAutoSichern<T>(stand: string, wert: T, senden: (wert: T, basis: T) => Promise<boolean>, warten = 1200) {
  const basis = useRef<{ stand: string; wert: T }>({ stand, wert });
  const aktuell = useRef({ stand, wert, senden });
  aktuell.current = { stand, wert, senden };
  const aktiv = useRef<Promise<boolean> | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehlStand, setFehlStand] = useState<string | null>(null);

  const sichern = useCallback((): Promise<boolean> => {
    if (aktiv.current) return aktiv.current.then(() => sichern());
    const { stand: s, wert: w, senden: sende } = aktuell.current;
    if (s === basis.current.stand) return Promise.resolve(true);
    setLaeuft(true);
    const p = sende(w, basis.current.wert)
      .then((ok) => {
        if (ok) { basis.current = { stand: s, wert: w }; setFehlStand(null); } else setFehlStand(s);
        return ok;
      }, () => { setFehlStand(s); return false; })
      .finally(() => { aktiv.current = null; setLaeuft(false); });
    aktiv.current = p;
    return p;
  }, []);

  /** Den aktuellen Stand als gespeichert ansehen — nach dem Löschen oder Verwerfen. */
  const verwerfen = useCallback(() => {
    basis.current = { stand: aktuell.current.stand, wert: aktuell.current.wert };
    setFehlStand(null);
  }, []);

  useEffect(() => {
    if (stand === basis.current.stand || stand === fehlStand) return;
    const h = window.setTimeout(() => { void sichern(); }, warten);
    return () => window.clearTimeout(h);
  }, [stand, fehlStand, warten, sichern]);

  // Beim Aushängen nichts liegen lassen.
  useEffect(() => () => {
    if (aktuell.current.stand !== basis.current.stand) void sichern();
  }, [sichern]);

  const anzeige: SichernAnzeige = fehlStand !== null && fehlStand === stand
    ? "fehler"
    : laeuft || stand !== basis.current.stand ? "speichert" : "gespeichert";
  return { sichern, verwerfen, anzeige };
}

const EINZELN = ["pause_stunden", "stundenlohn", "zusatzkosten", "note", "confirm_status", "einladungs_status"] as const;

/**
 * Was von den Einsätzen hinausgeht: neue Personen vollständig, bestehende nur mit
 * Zeiten (die braucht die Doppelbelegungsprüfung) und den Feldern, die sich seit
 * dem letzten Speichern geändert haben. Vergütungsart und Pauschale nur zusammen —
 * der Server löst die Pauschale sonst vom Einsatz.
 */
export function slotAenderungen(neu: UpdateTerminSlot[], alt: UpdateTerminSlot[]): UpdateTerminSlot[] {
  return neu.map((s) => {
    const a = alt.find((x) => x.person_id === s.person_id);
    if (!a) return s;
    const out: UpdateTerminSlot = { person_id: s.person_id, person_typ: s.person_typ, start_at: s.start_at, end_at: s.end_at };
    for (const k of EINZELN) {
      if (s[k] !== a[k]) (out as Record<string, unknown>)[k] = s[k];
    }
    if (s.verguetungsart !== a.verguetungsart || s.pauschale_id !== a.pauschale_id) {
      out.verguetungsart = s.verguetungsart;
      out.pauschale_id = s.pauschale_id;
    }
    return out;
  });
}
